import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import commonActions from "../../../Actions/commonActions";
import subscriptionActions from "../../../Actions/Voss/subscriptionActions";
import Modals from "../../../Constants/modals";
import { UserRoles } from "../../../Constants/userRoles";
import useModal from "../../../Hooks/useModal";
import useUserAccess from "../../../Hooks/useUserAccess";
import helpers from "../../../Utils";
import { Checkbox } from "../../FormComponents/Checkbox";
import { Dropdown } from "../../FormComponents/Dropdown";
import { ReadOnlyField } from "../../FormComponents/Input/Input";
import Editor from "../../Fundamentals/Form/Editor";
import StandardModal from "../../Fundamentals/Modal/StandardModal";
import UserSearchNew from "../../Search/UserSearchNew";

const ChangeItem = ({ label, oldValue, newValue }) => (
    <div>
        <label>{label}</label>
        <div className="d-flex gap-12 align-items-center">
            <div className="text-warning w-100"><ReadOnlyField value={oldValue} /></div>
            <span>→</span>
            <div className="text-success w-100"><ReadOnlyField value={newValue} /></div>
        </div>
    </div>
);

const ConfirmSubscriptionChangeModal = ({ subscription, form, offer, reloadData }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { closeModal } = useModal();
    const isModifying = useSelector(state => state.CommonReducer.dataLoading.modifySubscription);
    const { hasReadWriteAccess } = useUserAccess();

    const [orderType, setOrderType] = useState(form.data.orderType || 'ordinary');
    const [sendEmail, setSendEmail] = useState(false);
    const [emailTemplate, setEmailTemplate] = useState(null);
    const templateLoading = useSelector(state => state.CommonReducer.dataLoading.changeEmailTemplate);
    const isLoading = isModifying || (sendEmail && templateLoading);

    const newTier = offer.tiers.find(t => t.id === form.data.tierId);
    const isDowngrade = subscription.tier.id !== form.data.tierId && (newTier?.rank ?? Infinity) < subscription.tier.rank;
    const needsEmail = isDowngrade || subscription.plan.id !== form.data.planId;

    useEffect(() => {
        if (needsEmail) {
            dispatch(subscriptionActions.getChangeEmailTemplate(form.data.id, form.data.tierId, form.data.planId))
                .then(template => template && setEmailTemplate(template));
        }
    }, []);

    const orderTypeOptions = [
        { id: 'ordinary', label: t('confirmordermodal.ordinary') },
        {
            id: 'other',
            label: t('confirmordermodal.orderforother'),
            disabled: !hasReadWriteAccess(UserRoles.OrderForOther)
        },
        { id: 'partner', label: t('confirmordermodal.partner') },
        { id: 'web', label: t('confirmordermodal.web') },
        { id: 'onlineSales', label: t('confirmordermodal.onlineorder') }
    ];

    const onSalesPersonChange = (salesPerson) => {
        dispatch(commonActions.changeMultiFormField(form.id, 'salesPersonEmail', salesPerson?.email || null));
    };

    useEffect(() => {
        if (orderType !== 'other' && form.data.salesPersonEmail) {
            dispatch(commonActions.changeMultiFormField(form.id, 'salesPersonEmail', null));
        }
    }, [orderType, form.data.salesPersonEmail, dispatch, form.id]);

    useEffect(() => {
        dispatch(commonActions.changeMultiFormField(form.id, 'orderType', orderType));
    }, [orderType, dispatch, form.id]);

    const handleConfirm = () => {
        dispatch(subscriptionActions.modifySubscription(form, sendEmail ? emailTemplate : null, () => {
            if (reloadData) reloadData();
            closeModal(Modals.ConfirmSubscriptionChange);
        }));
    };

    const handleCancel = () => {
        dispatch(commonActions.changeMultiFormField(form.id, 'salesPersonEmail', null));
        closeModal(Modals.ConfirmSubscriptionChange);
    };

    const isTierChanged = subscription.tier.id !== form.data.tierId;
    const isPlanChanged = subscription.plan.id !== form.data.planId;
    const newTierName = offer.tiers.find(t => t.id === form.data.tierId)?.name;
    const newPlanName = offer.plans.find(p => p.id === form.data.planId)?.name;

    return (
        <StandardModal title={t("confirmsubscriptionchange.title")} onClose={handleCancel} onSubmit={handleConfirm} isLoading={isLoading}>
            <div className="d-grid grid-cols-1 gap-16">
                    <div className="d-grid grid-cols-1 gap-16">
                        {isTierChanged && <ChangeItem label={t("common.tier")} oldValue={subscription.tier.name} newValue={newTierName} />}
                        {isPlanChanged && <ChangeItem label={t("common.plan")} oldValue={subscription.plan.name} newValue={newPlanName} />}
                        {form.data.endDate && <ChangeItem label={t("confirmsubscriptionchange.newEndDate")} oldValue={helpers.formatDate(subscription.bindingPeriod.endDate)} newValue={form.data.endDate} />}
                    </div>
                    <div className="form-group mb-0">
                        <Dropdown
                            options={orderTypeOptions}
                            value={orderType}
                            onChange={setOrderType}
                            property="label"
                            label={t('confirmordermodal.orderType')}
                            disabledOptionKey="disabled" />
                    </div>
                    {hasReadWriteAccess(UserRoles.OrderForOther) && orderType === 'other' && <div className="form-group mb-0">
                        <label>{t("confirmsubscriptionchange.seller")}</label>
                        <UserSearchNew onChange={onSalesPersonChange} />
                        <small>
                            {t("confirmsubscriptionchange.keepCurrentSeller")}
                        </small>
                    </div>}
                    {needsEmail && <Checkbox
                        label={t('confirmsubscriptionchange.sendEmail')}
                        checked={sendEmail}
                        onChange={setSendEmail}
                    />}
                    {needsEmail && sendEmail && <div className="form-group mb-0">
                        <label>{t('confirmsubscriptionchange.emailBody')}</label>
                        {emailTemplate && <Editor
                            readOnly={false}
                            defaultValue={emailTemplate?.body ?? ''}
                            onChange={value => setEmailTemplate({ ...emailTemplate, body: value })}
                        />}
                    </div>}
                </div>
        </StandardModal>
    );
};

export default ConfirmSubscriptionChangeModal;
